import { ClientCreation, ClientCreationWithErrors, ErrorClient, prettifyColumns } from '@Arti-zen/package-backoffice'
import { definitionFields } from './tableList/ClientListTable.definitionFields'
import TableInfinite from 'components/table/infinite/InfiniteTable'
import styled from 'styled-components'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Close } from '@mui/icons-material'

type TProps = {
  data: ClientCreationWithErrors[] | ClientCreation[]
  correctClient: ({
    corrections,
    client,
  }: {
    corrections: Partial<ClientCreation>
    client: ClientCreationWithErrors
  }) => void
  openForm: (client: ClientCreation | ClientCreationWithErrors, index: number) => void
  onModify: boolean
  replaceAllErrors: ({
    property,
    getCorrections,
    condition,
  }: {
    property: keyof ClientCreation
    condition?: (client: ClientCreation) => boolean
    getCorrections: (client: ClientCreation, error: ErrorClient | null) => Partial<ClientCreation> | null
  }) => void
}

export type ClientErrorIndex = Partial<Record<keyof ClientCreation, ErrorClient>> & {
  unknownSymbols?: (keyof ClientCreation)[]
}

export default function ClientListTable({ onModify, openForm, data, correctClient, replaceAllErrors }: TProps) {
  const [filteredBy, setFilteredBy] = useState<keyof ClientCreation | null>(null)

  const PAGE_SIZE = 200
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  const numberOfErrors = useMemo(() => {
    const counts = {
      id: 0,
      lastName: 0,
      firstName: 0,
      phone: 0,
      mobile: 0,
      email: 0,
      address: 0,
      additionalAddress: 0,
      isProfessional: 0,
      title: 0,
      siren: 0,
      postalCode: 0,
      city: 0,
      clientNotes: 0,
    }

    if (!data[0] || !('errors' in data[0])) return counts

    for (const client of data as ClientCreationWithErrors[]) {
      for (const error in client.errors) {
        counts[error]++
      }
    }

    return counts
  }, [data])

  const displayedData = useMemo(() => {
    if (!filteredBy || !('errors' in data?.[0])) {
      return data.map((client, index) => {
        return { ...client, id: client.id ?? index }
      })
    }

    return data
      .map((client, index) => {
        return { ...client, id: client.id ?? index }
      })
      .filter((client) => !!(client as ClientCreationWithErrors).errors?.[filteredBy])
  }, [data, filteredBy])

  const paginatedData = useMemo(() => displayedData.slice(0, visibleCount), [displayedData, visibleCount])

  useEffect(() => {
    setVisibleCount(PAGE_SIZE)
  }, [filteredBy, data.length])

  const nextPage = useCallback(() => {
    setVisibleCount((count) => Math.min(count + PAGE_SIZE, displayedData.length))
  }, [displayedData.length])

  const showErrors = useCallback((property?: keyof ClientCreation) => {
    setFilteredBy((current) => (current === property ? null : (property ?? null)))
  }, [])

  const seeErrorsButton = useCallback(
    ({ property }: { property: keyof ClientCreation }) => (
      <SeeErrorsText onClick={() => showErrors(filteredBy === property ? null : property)}>
        {`Voir les erreurs (${numberOfErrors[property]})`}
      </SeeErrorsText>
    ),
    [filteredBy]
  )

  const fields = useMemo(
    () =>
      definitionFields({
        hasId: onModify,
        openForm,
        correctClient,
        replaceAllErrors,
        numberOfErrors,
        seeErrorsButton,
      }),
    [onModify, numberOfErrors, replaceAllErrors]
  )

  const minWidth = useMemo(() => {
    const numberOfPropertiesToDisplay = onModify ? 14 : 13
    return numberOfPropertiesToDisplay * 200
  }, [])

  return (
    <>
      {filteredBy && (
        <Row>
          <p style={{ margin: 0 }}>Filtré par : {prettifyColumns[filteredBy]}</p>
          <ButtonRemoveFilter onClick={() => setFilteredBy(null)} fontSize="small" />
        </Row>
      )}
      <Container>
        <div style={{ minWidth, display: 'flex', flex: 1 }}>
          <TableInfinite<ClientCreationWithErrors>
            data={paginatedData}
            definitionFields={fields}
            isQueriedAllCompleted={visibleCount >= displayedData.length}
            nextPage={nextPage}
          />
        </div>
      </Container>
    </>
  )
}

const Container = styled.div`
  display: flex;
  overflow-x: auto;
  flex: 1;
`

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
  margin-bottom: 16px;
`

const ButtonRemoveFilter = styled(Close)`
  cursor: pointer;
  &:hover {
    color: red;
  }
`

const SeeErrorsText = styled.p`
  margin: 0;
  color: red;
  text-transform: none;
  cursor: pointer;
  &:hover {
    font-weight: bold;
  }
`
